import React, { useMemo, useState } from 'react';
import { ArrowDownIcon, ArrowUpDownIcon, ArrowUpIcon, DownloadIcon, LayoutGridIcon, ListIcon, MapPinIcon, SearchIcon, SearchXIcon, UserPlusIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Avatar } from '../components/ui/Avatar';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { FilterSelect } from '../components/ui/FilterSelect';
import { PageHeader } from '../components/ui/PageHeader';
import { StatusBadge } from '../components/ui/StatusBadge';
import { departmentNames, divisionNames, locationNames, statusNames } from '../data/org';
import { useHRStore } from '../hooks/useHRStore';
import type { Employee, Navigate } from '../types/hr';
import { displayName } from '../utils/employees';
import { formatDate } from '../utils/format';
type SortKey = 'name' | 'employeeNumber' | 'jobTitle' | 'department' | 'division' | 'location' | 'status' | 'hireDate';
type SortDir = 'asc' | 'desc';
const COLUMNS: {
  key: SortKey;
  label: string;
}[] = [{
  key: 'name',
  label: 'Name'
}, {
  key: 'employeeNumber',
  label: 'Employee #'
}, {
  key: 'jobTitle',
  label: 'Job Title'
}, {
  key: 'department',
  label: 'Department'
}, {
  key: 'division',
  label: 'Division'
}, {
  key: 'location',
  label: 'Location'
}, {
  key: 'status',
  label: 'Status'
}, {
  key: 'hireDate',
  label: 'Hire Date'
}];
const ALL = 'All';
export function Employees({
  onNavigate
}: {
  onNavigate: Navigate;
}) {
  const {
    employees
  } = useHRStore();
  const [query, setQuery] = useState('');
  const [department, setDepartment] = useState(ALL);
  const [division, setDivision] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [location, setLocation] = useState(ALL);
  const [view, setView] = useState<'table' | 'cards'>('table');
  const [sort, setSort] = useState<{
    key: SortKey;
    dir: SortDir;
  }>({
    key: 'employeeNumber',
    dir: 'asc'
  });
  const deptCount = new Set(employees.map(e => e.department)).size;
  const divCount = new Set(employees.map(e => e.division)).size;
  const locCount = new Set(employees.map(e => e.location)).size;
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = employees.filter(e => (department === ALL || e.department === department) && (division === ALL || e.division === division) && (status === ALL || e.status === status) && (location === ALL || e.location === location) && (!q || `${displayName(e)} ${e.jobTitle} ${e.employeeNumber} ${e.workEmail}`.toLowerCase().includes(q)));
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => sortValue(a, sort.key).localeCompare(sortValue(b, sort.key)) * dir);
  }, [employees, query, department, division, status, location, sort]);
  const hasFilters = Boolean(query) || [department, division, status, location].some(v => v !== ALL);
  const clearFilters = () => {
    setQuery('');
    setDepartment(ALL);
    setDivision(ALL);
    setStatus(ALL);
    setLocation(ALL);
  };
  const toggleSort = (key: SortKey) => setSort(prev => prev.key === key ? {
    key,
    dir: prev.dir === 'asc' ? 'desc' : 'asc'
  } : {
    key,
    dir: 'asc'
  });
  const open = (id: string) => onNavigate({
    page: 'profile',
    employeeId: id
  });
  return <div data-id="element-862" data-mp="3udu2knsyrwyt">
      <PageHeader eyebrow="People" title="Employees" description={`${employees.length} people across ${deptCount} departments, ${divCount} divisions and ${locCount} locations`} actions={<>
            <Button icon={DownloadIcon} onClick={() => toast.success('Roster export started', {
        description: `${filtered.length} employees · CSV`
      })} data-id="element-864" data-mp="3fp7osqizjd9p">
              Export
            </Button>
            <Button variant="primary" icon={UserPlusIcon} onClick={() => toast('Add employee', {
        description: 'New hires are created from the onboarding flow in Salesforce.'
      })} data-id="element-865" data-mp="36osz7eyznc0e">
              Add employee
            </Button>
          </>} data-id="element-863" data-mp="3ue3u4ftnqc6m" />

      <div className="rounded-2xl bg-white shadow-soft ring-1 ring-slate-900/[0.04]" data-id="element-866" data-mp="15n39w6k3mo53">
        <div className="flex flex-col gap-3 border-b border-slate-100 p-4 lg:flex-row lg:items-center" data-id="element-867" data-mp="19n3phuixp5dq">
          <div className="relative flex-1 lg:max-w-xs" data-id="element-868" data-mp="vvyw3a65t9bu">
            <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-869" data-mp="10vl7rf6cahij" />
            <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by name, title, #…" aria-label="Search employees" className="h-10 w-full rounded-xl border-0 bg-canvas pl-10 pr-3 text-sm text-navy ring-1 ring-inset ring-slate-200 placeholder:text-mutedlight focus:bg-white focus:outline-none focus:ring-2 focus:ring-azure" data-id="element-870" data-mp="lxsukto9m57i" />
          </div>
          <div className="grid flex-1 grid-cols-2 gap-2 md:grid-cols-4" data-id="element-871" data-mp="2cb9xgeaz9un1">
            <FilterSelect label="Department" allLabel="All departments" value={department} options={departmentNames} onChange={setDepartment} data-id="element-872" data-mp="2meoycs4bm53u" />
            <FilterSelect label="Division" allLabel="All divisions" value={division} options={divisionNames} onChange={setDivision} data-id="element-873" data-mp="2hh56uqim3vty" />
            <FilterSelect label="Status" allLabel="All statuses" value={status} options={statusNames} onChange={setStatus} data-id="element-874" data-mp="2v93bao1dp3js" />
            <FilterSelect label="Location" allLabel="All locations" value={location} options={locationNames} onChange={setLocation} data-id="element-875" data-mp="3556cnroeoy5o" />
          </div>
          <div className="flex items-center gap-2" data-id="element-876" data-mp="305tnv1dz5h0g">
            {hasFilters && <button type="button" onClick={clearFilters} className="px-2 text-xs font-medium text-azure hover:underline" data-id="element-877" data-mp="3e4tf8gsvga0b">
                Clear
              </button>}
            <div className="flex rounded-xl bg-canvas p-1 ring-1 ring-inset ring-slate-200" role="group" aria-label="View" data-id="element-878" data-mp="1jrbpcn4zv124">
              {([{
              id: 'table',
              icon: ListIcon,
              label: 'Table view'
            }, {
              id: 'cards',
              icon: LayoutGridIcon,
              label: 'Card view'
            }] as const).map(({
              id,
              icon: Icon,
              label
            }) => <button key={id} type="button" onClick={() => setView(id)} aria-pressed={view === id} aria-label={label} className={`flex h-8 w-8 items-center justify-center rounded-lg transition ${view === id ? 'bg-white text-navy shadow-sm' : 'text-muted hover:text-navy'}`} data-id="element-879" data-mp="3oswi1pbuwj5a">
                  <Icon className="h-4 w-4" data-id="element-880" data-mp="be9mhdmlb8ly" />
                </button>)}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between px-6 py-3 text-xs text-muted" data-id="element-881" data-mp="gd33hg135hqv">
          <span data-id="element-882" data-mp="le0gle78nlli">
            Showing <span className="font-semibold text-navy" data-id="element-883" data-mp="lamfsae83kg6">{filtered.length}</span> of {employees.length}
          </span>
          <span className="hidden sm:inline" data-id="element-884" data-mp="pbybmxnox1wt">Click a row to open the profile</span>
        </div>

        {filtered.length === 0 ? <div className="px-6 pb-6" data-id="element-885" data-mp="25ui38kbu47sx">
            <EmptyState icon={SearchXIcon} title="No employees match these filters" description="Try a different search or clear filters to see everyone." action={<Button size="sm" onClick={clearFilters} data-id="element-887" data-mp="1vywtu14fs595">
                  Clear filters
                </Button>} data-id="element-886" data-mp="25tgi1n13u4xh" />
          </div> : view === 'table' ? <div className="overflow-x-auto" data-id="element-888" data-mp="2onc7aj7x6fid">
            <table className="w-full min-w-[1000px] text-left text-sm" data-id="element-889" data-mp="2jtr7clmrnbgj">
              <thead data-id="element-890" data-mp="2evg26o7f1p4n">
                <tr className="border-y border-slate-100 bg-canvas/70" data-id="element-891" data-mp="2ew7vtthgdcsa">
                  {COLUMNS.map(c => {
                const active = sort.key === c.key;
                const SortIcon = active ? sort.dir === 'asc' ? ArrowUpIcon : ArrowDownIcon : ArrowUpDownIcon;
                return <th key={c.key} scope="col" aria-sort={active ? sort.dir === 'asc' ? 'ascending' : 'descending' : 'none'} className="px-6 py-3" data-id="element-892" data-mp="2fvqvzz331o1c">
                        <button type="button" onClick={() => toggleSort(c.key)} className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider transition ${active ? 'text-navy' : 'text-muted hover:text-navy'}`} data-id="element-893" data-mp="26vugoo2l21iy">
                          {c.label}
                          <SortIcon className={`h-3.5 w-3.5 ${active ? 'text-azure' : 'text-slate-300'}`} aria-hidden="true" data-id="element-894" data-mp="33jqytxdo54wa" />
                        </button>
                      </th>;
              })}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-895" data-mp="1e5lsofld8mwg">
                {filtered.map(e => <tr key={e.id} tabIndex={0} onClick={() => open(e.id)} onKeyDown={ev => {
              if (ev.key === 'Enter') open(e.id);
            }} className="cursor-pointer transition-colors hover:bg-azure/[0.04] focus:bg-azure/[0.06] focus:outline-none" data-id="element-896" data-mp="1j7upjn1d093u">
                    <td className="px-6 py-3.5" data-id="element-897" data-mp="1fovnb9sdt6v8">
                      <div className="flex items-center gap-3" data-id="element-898" data-mp="ryhz5ucx3s5x">
                        <Avatar name={displayName(e)} seed={e.id} size="sm" data-id="element-899" data-mp="rwougklv7i0k" />
                        <div className="min-w-0" data-id="element-900" data-mp="n19jc0f8s07u">
                          <p className="truncate font-medium text-navy" data-id="element-901" data-mp="11ob2y96tj2zm">{displayName(e)}</p>
                          <p className="truncate text-xs text-muted" data-id="element-902" data-mp="wxukubs1yt6x">{e.workEmail.split('@')[0]}@…</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-3.5 font-mono text-xs text-slate-500" data-id="element-903" data-mp="95xayq85yb8g">{e.employeeNumber}</td>
                    <td className="px-6 py-3.5 text-slate-700" data-id="element-904" data-mp="47ck3hesx3wo">{e.jobTitle}</td>
                    <td className="px-6 py-3.5 text-slate-700" data-id="element-905" data-mp="21xysj45vwyyq">{e.department}</td>
                    <td className="px-6 py-3.5 text-slate-600" data-id="element-906" data-mp="1x06stnz008jz">{e.division}</td>
                    <td className="px-6 py-3.5 text-slate-600" data-id="element-907" data-mp="1rzj458y8s3sx">{e.location}</td>
                    <td className="px-6 py-3.5" data-id="element-908" data-mp="1n1zdf9kcxyk3">
                      <StatusBadge status={e.status} data-id="element-909" data-mp="1n17v30frtxbr" />
                    </td>
                    <td className="px-6 py-3.5 text-slate-600" data-id="element-910" data-mp="1j0nvlq7q6hmg">{formatDate(e.hireDate)}</td>
                  </tr>)}
              </tbody>
            </table>
          </div> : <div className="grid grid-cols-1 gap-4 px-4 pb-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4" data-id="element-911" data-mp="19aumqwyywglw">
            {filtered.map(e => <button key={e.id} type="button" onClick={() => open(e.id)} className="group flex flex-col rounded-2xl bg-white p-5 text-left ring-1 ring-slate-200/80 transition hover:-translate-y-0.5 hover:shadow-lift hover:ring-azure/30" data-id="element-912" data-mp="zbvma1ocvr9z">
                <div className="flex items-start justify-between" data-id="element-913" data-mp="3aypddlg4k6tz">
                  <Avatar name={displayName(e)} seed={e.id} size="lg" data-id="element-914" data-mp="3b137ns2zdf0h" />
                  <StatusBadge status={e.status} data-id="element-915" data-mp="3fyuwgd8bwl0g" />
                </div>
                <p className="mt-4 font-semibold text-navy group-hover:text-azure" data-id="element-916" data-mp="3kxoe8huney6f">{displayName(e)}</p>
                <p className="text-sm text-slate-600" data-id="element-917" data-mp="2napcvtt0h227">{e.jobTitle}</p>
                <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 border-t border-slate-100 pt-3 text-xs text-muted" data-id="element-918" data-mp="2s8hexgnde4k4">
                  <span data-id="element-919" data-mp="2x7k8a3tzmmc9">{e.department}</span>
                  <span className="inline-flex items-center gap-1" data-id="element-920" data-mp="2x5r18nfgq03q">
                    <MapPinIcon className="h-3 w-3" aria-hidden="true" data-id="element-921" data-mp="31zc2qpg9lc7o" />
                    {e.location}
                  </span>
                  <span data-id="element-922" data-mp="24h29hf0ar74n">Since {formatDate(e.hireDate, 'MMM yyyy')}</span>
                </div>
              </button>)}
          </div>}
      </div>
    </div>;
}
function sortValue(e: Employee, key: SortKey): string {
  if (key === 'name') return `${e.lastName} ${e.firstName}`;
  return String(e[key]);
}